import React, { useCallback, useState } from "react";
import AiFeatureToolPrep from "../../shared/Popup/AiFeatureToolPrep";
import AiPresetCards from "../../shared/Popup/AiPresetCards";
import { presetTip } from "../../shared/Popup/presetTip";
import { relightSingle } from "../../../../../services/api/tools/relight";

const DIRECTIONS = [
  {
    id: "front",
    label: "Front",
    sub: "Even",
    tip: presetTip('Front — "Even"', [
      "Light from the camera direction",
      "Flattens shadows for a clean look",
    ]),
  },
  {
    id: "left",
    label: "Left",
    sub: "Side light",
    tip: presetTip('Left — "Side light"', [
      "Key light from the left",
      "Adds depth across the subject",
    ]),
  },
  {
    id: "right",
    label: "Right",
    sub: "Side light",
    tip: presetTip('Right — "Side light"', [
      "Key light from the right",
      "Useful for dramatic product shots",
    ]),
  },
  {
    id: "top",
    label: "Top",
    sub: "Overhead",
    tip: presetTip('Top — "Overhead"', [
      "Light from above",
      "Natural for product tabletop scenes",
    ]),
  },
];

const MOODS = [
  {
    id: "soft",
    label: "Soft",
    sub: "Gentle",
    tip: presetTip('Soft — "Gentle"', [
      "Diffused lighting",
      "Lower contrast, flattering look",
    ]),
  },
  {
    id: "natural",
    label: "Natural",
    sub: "Recommended",
    tip: presetTip('Natural — "Recommended"', [
      "Balanced contrast and color",
      "Default for most photos",
    ]),
  },
  {
    id: "dramatic",
    label: "Dramatic",
    sub: "High contrast",
    tip: presetTip('Dramatic — "High contrast"', [
      "Stronger highlights and shadows",
      "More cinematic mood",
    ]),
  },
];

/**
 * AI Tools → AI Relight prep.
 */
const RelightToolPrep = (props) => {
  const [directionId, setDirectionId] = useState("front");
  const [moodId, setMoodId] = useState("natural");
  const [prompt, setPrompt] = useState("");

  const handleGenerate = useCallback(
    async ({ token, file, image_url }) =>
      relightSingle(token, file, {
        direction: directionId,
        mood: moodId,
        prompt: prompt.trim() || undefined,
        output_format: "webp",
        ...(image_url ? { image_url } : {}),
      }),
    [directionId, moodId, prompt]
  );

  return (
    <AiFeatureToolPrep
      {...props}
      onGenerate={handleGenerate}
      generateFileName="relight-input.png"
      toolKey="ai_relight"
      title="AI Relight"
      aka="AI Lighting"
      bio="Change lighting and mood without reshooting."
      emptyPreviewLabel="Upload an image to preview relight"
      loadingLabel="Relighting…"
      emptyFooterHint="Upload an image, pick light direction, then generate."
      renderControls={({ busy, leftPanelRef }) => (
        <>
          <div className="aftp-section">
            <p className="aftp-label">Light direction</p>
            <AiPresetCards
              presets={DIRECTIONS}
              selectedId={directionId}
              columns={2}
              ariaLabel="Light direction"
              boundaryRef={leftPanelRef}
              disabled={busy}
              onSelect={(id) => {
                setDirectionId(id);
              }}
            />
          </div>
          <div className="aftp-section">
            <p className="aftp-label">Mood</p>
            <AiPresetCards
              presets={MOODS}
              selectedId={moodId}
              ariaLabel="Lighting mood"
              boundaryRef={leftPanelRef}
              disabled={busy}
              onSelect={(id) => {
                setMoodId(id);
              }}
            />
          </div>
          <div className="aftp-section">
            <label className="aftp-label" htmlFor="aftp-rl-prompt">
              Prompt <span className="aftp-optional">(optional)</span>
            </label>
            <textarea
              id="aftp-rl-prompt"
              className="aftp-input"
              rows={3}
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
              }}
              placeholder="e.g. warm sunset light, cool studio key light"
              disabled={busy}
            />
          </div>
        </>
      )}
    />
  );
};

export default RelightToolPrep;
